import React, { useCallback, useEffect, useState } from 'react';
import { LeasingGroupDto, LeasingGroupItemDto } from '../../../../modelView';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, FormGroup, Grid, Paper, Switch, Tooltip } from '@mui/material';
import { TabPanel } from '@mui/lab';
import InsuranceDetails from '../../../financeCalculation/insuranceDetails';
import {
  CalculationDomainEnum,
  CalculationResult,
  CalculationVehicleTypeEnum,
  LeasingTypeEnum,
  ParamTypeEnum,
} from '../../../../api/graphql/generated/schema';
import { defaultVat } from '../../../../utils/const';
import { CalculationConfigDto } from '../../../../modelView/finance/calculation/calculationConfig';
import LeasingGroupTable from './LeasingGroupTable';
import { CardContext, InsuranceConfig } from '../../../../modelView/finance/calculation/interfaces';
import { useCalculateVehicleFinanceLazyQuery } from '../../../../api/graphql/generated/hooks';
import { useDispatch } from 'react-redux';
import { setLoadingStatus } from '../../../../redux/loading';
import { setSnackbar, SnackbarStates } from '../../../../redux/snackbar';
import { getGQLErrorMessages } from '../../../../utils/graphGL/graphQLHelper';

// TODO get from config file
export const calculationBrands = ['SKODA', 'VWG'];
export const DEFAULT_PAYMENT_PERIODS = [24, 36, 48, 60];

const initCalculationInput = {
  domain: CalculationDomainEnum.Leasing,
  params: [{ paymentPeriod: 24, totalKmPerYear: 35000 }],
};

export const initInsuranceInput = {
  insurance: [],
  deductible: [],
  windshieldLimit: [],
};

export const acceptableCalcErrorCodes = ['400', '404'];

export default function LeasingGroupTab(props: {
  cardContext: CardContext;
  domain: CalculationDomainEnum;
  handleUpdate: (items: LeasingGroupDto) => void;
  leasingGroup: LeasingGroupDto;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [useCalculator, setUseCalculator] = useState<boolean>(Boolean(props.leasingGroup.leasingCalculationParams));

  // State for insurance mapping
  const [insuranceConfigOptions, setInsuranceConfigOptions] = useState<InsuranceConfig>(initInsuranceInput);
  const [productDisclaimer, setProductDisclaimer] = useState('');

  // State for tabs input
  const [paymentPeriodOptions, setPaymentPeriodOptions] = useState<Array<number> | undefined>(undefined);

  // State for showing calc input
  const [disableCalculatorUsage, setDisableCalculatorUsage] = useState<boolean>(false);

  const [
    gqlCalculateFinances,
    { variables: calculationVariables, error: errorCalculationInfo, loading: loadingCalculationInfo },
  ] = useCalculateVehicleFinanceLazyQuery({
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    dispatch(setLoadingStatus(loadingCalculationInfo));
  }, [loadingCalculationInfo, dispatch]);

  useEffect(() => {
    if (errorCalculationInfo) {
      if (acceptableCalcErrorCodes.some((code) => errorCalculationInfo.message.includes(code))) {
        setDisableCalculatorUsage(true);
        return;
      }
      if (errorCalculationInfo.message.includes('monthPaymentWithInsuranceVAT is not a number')) {
        const insurance = insuranceConfigOptions.insurance.find(
          (i) => i.key === calculationVariables?.input.insuranceId,
        );
        const insuranceId = calculationVariables?.input.insuranceId;
        if (!insurance || !insuranceId) return;
        dispatch(
          setSnackbar(
            true,
            SnackbarStates.ERROR,
            t('userError.financeCalculation.fetchInsurancePackagesFailed', {
              insurance: insurance.label,
            }),
          ),
        );
        return;
      }
      dispatch(setSnackbar(true, SnackbarStates.ERROR, getGQLErrorMessages(errorCalculationInfo)));
    }
  }, [errorCalculationInfo, insuranceConfigOptions, calculationVariables, t, dispatch]);

  useEffect(() => {
    if (cardDefinitionIsValid()) {
      fetchPossibleValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardDefinitionIsValid = () => {
    return (
      props.cardContext.vehicleType === CalculationVehicleTypeEnum.New &&
      props.cardContext.brandId &&
      calculationBrands.includes(props.cardContext.brandId)
    );
  };

  const getGQLCardInput = useCallback(() => {
    const [modelKey] = props.cardContext.modelKey.split('/');
    return {
      vehicleType: props.cardContext.vehicleType,
      modelKey: modelKey,
      modelYear: props.cardContext.modelYear,
      totalModelPrice: props.cardContext.totalModelPrice,
      regDateFirst: props.cardContext.regDateFirst,
    };
  }, [props.cardContext]);

  const fetchInsurancePackagesById = useCallback(
    async (insuranceId: number) => {
      const result = await gqlCalculateFinances({
        variables: {
          input: {
            ...initCalculationInput,
            ...getGQLCardInput(),
            insuranceId,
          },
        },
      });
      const calculation = result.data?.calculateVehicleFinance?.calculation;
      if (!calculation) {
        return null;
      }
      const calResult = calculation.calculationResults[0];
      const insurancePackage = calResult.insurancePackageIdParam;
      if (!insurancePackage || !insurancePackage.possibleValues) {
        return null;
      }
      const packageOptions = insurancePackage.possibleValues.map((pkgId) => ({
        key: pkgId,
        label: insurancePackage.labels ? insurancePackage.labels[String(pkgId)] : String(pkgId),
      }));
      return packageOptions;
    },
    [gqlCalculateFinances, getGQLCardInput],
  );

  const fetchPossibleValues = useCallback(
    async (productId?: string) => {
      const gqlCalculationInfo = await gqlCalculateFinances({
        variables: {
          input: {
            ...initCalculationInput,
            ...getGQLCardInput(),
            productId: productId || undefined,
          },
        },
      });
      const calculations = gqlCalculationInfo.data?.calculateVehicleFinance?.calculation;
      if (!calculations) {
        return;
      }
      setProductDisclaimer(gqlCalculationInfo.data?.calculateVehicleFinance?.productDisclaimer || '');
      const result = calculations.calculationResults[0];
      const insurance = result?.insuranceIdParam;
      if (!insurance || !insurance.possibleValues) {
        return;
      }
      let windshieldLimitOptions: Array<number> = [];
      let deductibleOptions: Array<number> = [];

      // For each possible insuranceId, fetch its package list
      const insuranceOptions = await Promise.all(
        insurance.possibleValues.map(async (insuranceId) => {
          const options = await fetchInsurancePackagesById(insuranceId);
          return {
            key: insuranceId,
            label: insurance.labels ? insurance.labels[String(insuranceId)] : String(insuranceId),
            packageOptions: options || [],
          };
        }),
      );
      // payment period available values
      const paymentPeriodParam = result.paymentPeriodParam;
      if (paymentPeriodParam?.possibleValues) {
        if (paymentPeriodParam.paramInputType === ParamTypeEnum.Edit) {
          // no other possible values are set, so default to preset ones
          setPaymentPeriodOptions(DEFAULT_PAYMENT_PERIODS);
        } else {
          setPaymentPeriodOptions(paymentPeriodParam.possibleValues);
        }
      }
      // deductible available values
      const insuranceDeductibleParam = result.insuranceDeductibleParam;
      if (insuranceDeductibleParam?.possibleValues) {
        deductibleOptions = insuranceDeductibleParam.possibleValues;
      }
      // windshield limit available values
      const insuranceWindshieldLimitParam = result.insuranceWindshieldLimitParam;
      if (insuranceWindshieldLimitParam?.possibleValues) {
        windshieldLimitOptions = insuranceWindshieldLimitParam.possibleValues;
      }
      setInsuranceConfigOptions({
        insurance: insuranceOptions,
        deductible: deductibleOptions,
        windshieldLimit: windshieldLimitOptions,
      });
    },
    [gqlCalculateFinances, getGQLCardInput, fetchInsurancePackagesById],
  );

  const constructCreditItemDto = (calculations: CalculationResult): LeasingGroupItemDto => {
    const month = calculations.paymentPeriodParam.usedValue || 0;
    // advancePaymentParam is returned as integer representing percentage but dto expects a decimal value
    const totalKmPerYear = calculations.totalKmPerYearParam.usedValue || 0;
    const monthlyInstalment = Math.round(calculations.monthlyInstalment);
    const payment =
      props.leasingGroup.type === LeasingTypeEnum.Crn || props.leasingGroup.type === LeasingTypeEnum.ContraAccountCrn
        ? Math.round(monthlyInstalment / (1.0 + defaultVat))
        : monthlyInstalment;
    return new LeasingGroupItemDto(month, totalKmPerYear, payment);
  };

  const handleCalculationUpdate = (groupItems: Array<LeasingGroupItemDto>, calculationParams: CalculationConfigDto) => {
    fetchPossibleValues(calculationParams.productId);
    props.handleUpdate(new LeasingGroupDto(props.leasingGroup.type, [...groupItems], calculationParams));
  };

  const updateCalculatorUsage = (use: boolean) => {
    if (!use) {
      props.handleUpdate(new LeasingGroupDto(props.leasingGroup.type, props.leasingGroup.items));
    }
    setUseCalculator(use);
  };

  return (
    <TabPanel value={props.leasingGroup.type}>
      {cardDefinitionIsValid() && (
        <Grid container>
          <Tooltip title={disableCalculatorUsage ? t('userError.financeCalculation.calculationFailed') : ''}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    value={useCalculator}
                    checked={useCalculator}
                    disabled={disableCalculatorUsage}
                    onChange={(event, checked) => updateCalculatorUsage(checked)}
                  />
                }
                label={t('fieldName.useCalculator')}
              />
            </FormGroup>
          </Tooltip>
        </Grid>
      )}
      {cardDefinitionIsValid() && useCalculator && (
        <Paper sx={{ margin: '24px', padding: '16px' }}>
          <InsuranceDetails
            cardContext={props.cardContext}
            insuranceConfigOptions={insuranceConfigOptions}
            financeGroup={props.leasingGroup}
            domain={props.domain}
            productDisclaimer={productDisclaimer}
            // @ts-ignore
            handleUpdate={handleCalculationUpdate}
            constructItemDto={constructCreditItemDto}
            financeGroupCalculationParams={props.leasingGroup.leasingCalculationParams}
          />
        </Paper>
      )}
      <LeasingGroupTable
        leasingGroup={props.leasingGroup}
        handleUpdate={props.handleUpdate}
        monthOptions={useCalculator ? paymentPeriodOptions : undefined}
      />
    </TabPanel>
  );
}
